import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Check, Loader2, Circle, AlertTriangle } from 'lucide-react';
import { cn } from '../utils/cn';

export default function ProgressIndicator({ phases, progress }) {
  const currentIndex = phases.findIndex(p => p.key === progress.phase);
  const [recentCompanies, setRecentCompanies] = useState([]);

  // Track newly discovered companies
  useEffect(() => {
    if (progress.newCompany) {
      setRecentCompanies(prev => {
        const updated = [progress.newCompany, ...prev.filter(c => c !== progress.newCompany)];
        return updated.slice(0, 5); // Keep last 5 companies
      });
    }
  }, [progress.newCompany]);

  // Clear companies when phase changes away from finding_companies
  useEffect(() => {
    if (progress.phase !== 'finding_companies') {
      setRecentCompanies([]);
    }
  }, [progress.phase]);

  return (
    <div className="bg-card rounded-xl p-6 shadow-card">
      <div className="space-y-4">
        {phases.map((phase, index) => {
          const isCompleted = index < currentIndex;
          const isCurrent = phase.key === progress.phase;
          const isPending = index > currentIndex;

          return (
            <div key={phase.key} className="flex items-start gap-4">
              {/* Status Icon */}
              <div className={cn(
                "w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5",
                isCompleted && "bg-success text-success-foreground",
                isCurrent && "bg-secondary text-secondary-foreground",
                isPending && "bg-muted text-muted-foreground"
              )}>
                {isCompleted && <Check className="w-4 h-4" />}
                {isCurrent && (
                  <motion.div
                    animate={{ rotate: 360 }}
                    transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                  >
                    <Loader2 className="w-4 h-4" />
                  </motion.div>
                )}
                {isPending && <Circle className="w-4 h-4" />}
              </div>

              {/* Phase Label and Details */}
              <div className="flex-1 min-w-0">
                <p className={cn(
                  "font-medium",
                  isCompleted && "text-success",
                  isCurrent && "text-secondary",
                  isPending && "text-muted-foreground"
                )}>
                  {phase.label}
                </p>
                {isCurrent && progress.message && (
                  <motion.p
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="text-sm text-muted-foreground"
                  >
                    {progress.message}
                  </motion.p>
                )}

                {/* Show warning if limits exceeded */}
                {isCurrent && progress.warning && (
                  <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="flex items-center gap-2 mt-1 text-sm text-warning"
                  >
                    <AlertTriangle className="w-3 h-3" />
                    <span>{progress.warning}</span>
                  </motion.div>
                )}

                {/* Show recently found companies during finding_companies phase */}
                {isCurrent && phase.key === 'finding_companies' && recentCompanies.length > 0 && (
                  <div className="mt-2 space-y-1">
                    <AnimatePresence mode="popLayout">
                      {recentCompanies.map((company, idx) => (
                        <motion.div
                          key={company}
                          initial={{ opacity: 0, x: -20 }}
                          animate={{ opacity: 1 - (idx * 0.15), x: 0 }}
                          exit={{ opacity: 0, x: 20 }}
                          className="text-xs text-muted-foreground flex items-center gap-1"
                        >
                          <span className="text-success">+</span>
                          <span className="truncate">{company}</span>
                        </motion.div>
                      ))}
                    </AnimatePresence>
                  </div>
                )}
              </div>

              {/* Progress Count */}
              {isCurrent && progress.total > 0 && (
                <div className="text-sm text-muted-foreground whitespace-nowrap">
                  {progress.processed}/{progress.total}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
